import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { axiosInstance } from "../App";
import { Users, Plus, Loader2, Mail, Edit, Trash2, ArrowLeft, UserCog } from "lucide-react";
import { toast } from "sonner";

function UserManagement() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createUserForm, setCreateUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "personnel"
  });
  const [editUserForm, setEditUserForm] = useState({
    name: "",
    email: "",
    role: ""
  });

  useEffect(() => {
    // Check if user is admin
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    if (userData.role !== "admin") {
      toast.error(t('accessDenied'));
      navigate("/dashboard");
      return;
    }
    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/admin/users");
      setUsers(response.data);
    } catch (error) {
      toast.error(t('usersLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post("/admin/users", null, {
        params: createUserForm
      });
      toast.success(response.data.message || t('userCreated'));
      setCreateUserDialogOpen(false);
      setCreateUserForm({ name: "", email: "", password: "", role: "personnel" });
      await fetchUsers();
    } catch (error) {
      console.error("User creation error:", error);
      toast.error(error.response?.data?.detail || t('userCreateFailed'));
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserForm({
      name: user.name,
      email: user.email,
      role: user.role
    });
    setEditUserDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.put(`/admin/users/${selectedUser.id}`, null, {
        params: editUserForm
      });
      toast.success(response.data.message || t('userUpdated'));
      setEditUserDialogOpen(false);
      setSelectedUser(null);
      setEditUserForm({ name: "", email: "", role: "" });
      await fetchUsers();
    } catch (error) {
      console.error("User update error:", error);
      toast.error(error.response?.data?.detail || t('userUpdateFailed'));
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`${user.name} kullanıcısını silmek istediğinizden emin misiniz?`)) {
      return;
    }
    
    try {
      const response = await axiosInstance.delete(`/admin/users/${user.id}`);
      toast.success(response.data.message || t('userDeleted'));
      await fetchUsers();
    } catch (error) {
      console.error("User delete error:", error);
      toast.error(error.response?.data?.detail || t('userDeleteFailed'));
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: "Admin", color: "bg-red-100 text-red-800" },
      manager: { label: t('manager'), color: "bg-blue-100 text-blue-800" },
      engineer: { label: t('engineer'), color: "bg-purple-100 text-purple-800" },
      personnel: { label: t('personnel'), color: "bg-gray-100 text-gray-800" },
      member: { label: t('personnel'), color: "bg-gray-100 text-gray-800" }  // Backward compatibility
    };
    const config = roleConfig[role] || { label: role, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 text-xs rounded ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getRoleStats = () => {
    const stats = {
      manager: 0,
      engineer: 0,
      personnel: 0,
      member: 0  // Backward compatibility
    };
    users.forEach(user => {
      if (user.role !== "admin") {
        if (user.role === "member") {
          stats.personnel = (stats.personnel || 0) + 1;
        } else {
          stats[user.role] = (stats[user.role] || 0) + 1;
        }
      }
    });
    return stats;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const roleStats = getRoleStats();

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/milestora-logo-light.png" 
                alt="Milestora" 
                className="h-20 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: '#1F2937' }}>
                  {t('userManagement')}
                </h1>
                <p className="text-sm text-gray-500 mt-1">{t('members')}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('backToDashboard')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">{t('totalUsers')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-500" />
                <div className="text-2xl font-bold">{users.length}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">{t('manager')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <UserCog className="h-4 w-4 text-blue-500" />
                <div className="text-2xl font-bold">{roleStats.manager}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">{t('engineer')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <UserCog className="h-4 w-4 text-purple-500" />
                <div className="text-2xl font-bold">{roleStats.engineer}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">{t('personnel')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <UserCog className="h-4 w-4 text-gray-500" />
                <div className="text-2xl font-bold">{roleStats.personnel}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('users')}</CardTitle>
                <CardDescription>{t('allCompanyUsers')}</CardDescription>
              </div>
              <Button onClick={() => setCreateUserDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('newUser')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {users.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Henüz kullanıcı eklenmemiş
                </div>
              ) : (
                users.map((user) => (
                  <div key={user.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{user.name}</h3>
                          {getRoleBadge(user.role)}
                        </div>
                        <div className="text-sm text-gray-600 flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {user.email}
                        </div>
                        <div className="text-xs text-gray-400">
                          Oluşturulma: {new Date(user.created_at).toLocaleString('tr-TR')}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {user.role !== "admin" && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditUser(user)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Düzenle
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteUser(user)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {user.role === "admin" && (
                          <span className="text-xs text-gray-400 self-center px-3">
                            Admin düzenlenemez
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create User Dialog */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('createNewUser')}</DialogTitle>
            <DialogDescription>
              {t('createUserForCompany')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-user-name">{t('fullName')} *</Label>
              <Input
                id="create-user-name"
                value={createUserForm.name}
                onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                required
                placeholder={t('enterName')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-user-email">{t('email')} *</Label>
              <Input
                id="create-user-email"
                type="email"
                value={createUserForm.email}
                onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                required
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-user-password">{t('password')} *</Label>
              <Input
                id="create-user-password"
                type="password"
                value={createUserForm.password}
                onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                required
                placeholder={t('strongPassword')}
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-user-role">{t('role')} *</Label>
              <Select
                value={createUserForm.role}
                onValueChange={(value) => setCreateUserForm({ ...createUserForm, role: value })}
              >
                <SelectTrigger id="create-user-role">
                  <SelectValue placeholder={t('selectRole')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">{t('manager')}</SelectItem>
                  <SelectItem value="engineer">{t('engineer')}</SelectItem>
                  <SelectItem value="personnel">{t('personnel')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {t('roleDescriptions')}
              </p>
            </div>
            <Button type="submit" className="w-full">
              {t('createUser')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('editUserTitle')}</DialogTitle>
            <DialogDescription>
              {t('userUpdated')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-user-name">{t('fullName')} *</Label>
              <Input
                id="edit-user-name"
                value={editUserForm.name}
                onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                required
                placeholder={t('enterName')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-user-email">{t('email')} *</Label>
              <Input
                id="edit-user-email"
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                required
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-user-role">{t('role')} *</Label>
              <Select
                value={editUserForm.role}
                onValueChange={(value) => setEditUserForm({ ...editUserForm, role: value })}
              >
                <SelectTrigger id="edit-user-role">
                  <SelectValue placeholder={t('selectRole')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">{t('manager')}</SelectItem>
                  <SelectItem value="engineer">{t('engineer')}</SelectItem>
                  <SelectItem value="personnel">{t('personnel')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full">
              {t('updateUser')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UserManagement;
